/*
 * When-required medicines on the resident's screen.
 *
 * Each one says when it can next be given and how much has been given in 24 hours, so the carer
 * can see the answer before they ask. Afterwards it asks whether it worked.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';
import type { PrnMedicine, Witness } from './api.ts';
import { Overlay } from './overlay.tsx';

export function PrnSection({ residentId, homeId }: { residentId: string; homeId: string }) {
  const [medicines, setMedicines] = useState<PrnMedicine[] | null>(null);
  const [codes, setCodes] = useState<Record<string, string>>({});
  const [giving, setGiving] = useState<PrnMedicine | null>(null);
  const [followUp, setFollowUp] = useState<{ id: string; name: string } | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ medicines: PrnMedicine[]; codes: Record<string, string> }>(
        `/api/residents/${residentId}/prn`,
      );
      setMedicines(data.medicines);
      setCodes(data.codes);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the when-required medicines.');
    }
  }, [residentId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!medicines || !medicines.length) return null;

  return (
    <>
      <h2>When required</h2>
      {error ? <p className="error" role="alert">{error}</p> : null}
      <ul className="list">
        {medicines.map((med) => (
          <li key={med.medicineId}>
            <div className="row static column dose">
              <div className="row-line">
                <span className="row-main">{med.name}</span>
                <span className="time">{med.doseText}</span>
              </div>
              <p className="muted small">{med.directions}</p>

              <div className="chips">
                {med.reason ? <span className="chip">For {med.reason.toLowerCase()}</span> : null}
                {med.controlledDrug ? <span className="chip">Controlled drug</span> : null}
                {med.minGapMins ? <span className="chip">At least {formatGap(med.minGapMins)} apart</span> : null}
                {med.max24h !== null ? (
                  <span className={`chip ${med.wouldExceed ? 'warn' : ''}`}>
                    {med.given24h} of {med.max24h} in 24 hours
                  </span>
                ) : null}
              </div>

              {med.lastGivenAt ? (
                <p className="muted small">
                  Last given at {formatTime(med.lastGivenAt)}
                  {med.lastGivenBy ? ` by ${med.lastGivenBy}` : ''}
                </p>
              ) : (
                <p className="muted small">Not given in the last 24 hours.</p>
              )}

              {med.blockedReason ? <p className="notice warn">{med.blockedReason}</p> : null}

              {med.awaitingOutcome.map((waiting) => (
                <p className="notice" key={waiting.administrationId}>
                  Given {waiting.hoursAgo === 0 ? 'less than an hour' : `${waiting.hoursAgo} hours`} ago. Did it work?{' '}
                  <button
                    type="button"
                    className="quiet"
                    onClick={() => setFollowUp({ id: waiting.administrationId, name: med.name })}
                  >
                    Record the follow-up
                  </button>
                </p>
              ))}

              <div className="row-actions">
                <button type="button" onClick={() => setGiving(med)}>Record</button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {giving ? (
        <GivePrnDialog
          med={giving}
          homeId={homeId}
          codes={codes}
          onClose={() => setGiving(null)}
          onDone={() => {
            setGiving(null);
            void load();
          }}
        />
      ) : null}

      {followUp ? (
        <OutcomeDialog
          administrationId={followUp.id}
          name={followUp.name}
          onClose={() => setFollowUp(null)}
          onDone={() => {
            setFollowUp(null);
            void load();
          }}
        />
      ) : null}
    </>
  );
}

function GivePrnDialog({
  med,
  homeId,
  codes,
  onClose,
  onDone,
}: {
  med: PrnMedicine;
  homeId: string;
  codes: Record<string, string>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [outcome, setOutcome] = useState<'given' | 'not_given'>(med.blockedReason ? 'not_given' : 'given');
  const [code, setCode] = useState('R');
  const [reason, setReason] = useState(med.reason ? `In ${med.reason.toLowerCase()}` : '');
  const [note, setNote] = useState('');
  const [witnessId, setWitnessId] = useState('');
  const [witnesses, setWitnesses] = useState<Witness[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!med.controlledDrug) return;
    api
      .get<{ witnesses: Witness[] }>(`/api/homes/${homeId}/witnesses`)
      .then((data) => setWitnesses(data.witnesses))
      .catch(() => setWitnesses([]));
  }, [med.controlledDrug, homeId]);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError('');
      try {
        await api.post('/api/prn', {
          medicineId: med.medicineId,
          outcome,
          code: outcome === 'not_given' ? code : undefined,
          reason,
          note,
          witnessId: witnessId || undefined,
        });
        onDone();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      } finally {
        setBusy(false);
      }
    },
    [med, outcome, code, reason, note, witnessId, onDone],
  );

  return (
    <Overlay onClose={onClose}>
      <form className="card wide" onSubmit={submit}>
        <h2>{med.name}</h2>
        <p className="muted">{med.directions}</p>

        {med.blockedReason ? <p className="notice warn">{med.blockedReason}</p> : null}

        <fieldset>
          <legend>What happened</legend>
          <label className="check">
            <input
              type="radio"
              name="prn-outcome"
              checked={outcome === 'given'}
              disabled={!!med.blockedReason}
              onChange={() => setOutcome('given')}
            />
            <span>
              <strong>Given</strong>
              {med.blockedReason ? <span className="muted small"> not allowed yet</span> : null}
            </span>
          </label>
          <label className="check">
            <input type="radio" name="prn-outcome" checked={outcome === 'not_given'} onChange={() => setOutcome('not_given')} />
            <span><strong>Asked for, but not given</strong></span>
          </label>
        </fieldset>

        {outcome === 'given' ? (
          <>
            <label htmlFor="prn-reason">Why was it needed?</label>
            <input id="prn-reason" required value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="In pain in her left hip" />
          </>
        ) : (
          <>
            <label htmlFor="prn-code">Reason</label>
            <select id="prn-code" value={code} onChange={(e) => setCode(e.target.value)}>
              {Object.entries(codes)
                .filter(([letter]) => letter !== 'V')
                .map(([letter, label]) => (
                  <option key={letter} value={letter}>{letter} — {label}</option>
                ))}
            </select>
          </>
        )}

        {med.controlledDrug && outcome === 'given' ? (
          <>
            <label htmlFor="prn-witness">Witness</label>
            <select id="prn-witness" required value={witnessId} onChange={(e) => setWitnessId(e.target.value)}>
              <option value="">Choose who watched this</option>
              {witnesses.map((w) => (
                <option key={w.id} value={w.id}>{w.name} — {w.jobTitle}</option>
              ))}
            </select>
          </>
        ) : null}

        <label htmlFor="prn-note">Note (optional)</label>
        <input id="prn-note" value={note} onChange={(e) => setNote(e.target.value)} />

        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="row-actions">
          <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save this record'}</button>
          <button type="button" className="quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Overlay>
  );
}

function OutcomeDialog({
  administrationId,
  name,
  onClose,
  onDone,
}: {
  administrationId: string;
  name: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [worked, setWorked] = useState(true);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError('');
      try {
        await api.post(`/api/prn/${administrationId}/outcome`, { worked, note });
        onDone();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      } finally {
        setBusy(false);
      }
    },
    [administrationId, worked, note, onDone],
  );

  return (
    <Overlay onClose={onClose}>
      <form className="card wide" onSubmit={submit}>
        <h2>Did it work?</h2>
        <p className="muted">{name}</p>

        <fieldset>
          <legend>After that dose</legend>
          <label className="check">
            <input type="radio" name="worked" checked={worked} onChange={() => setWorked(true)} />
            <span><strong>Yes, it helped</strong></span>
          </label>
          <label className="check">
            <input type="radio" name="worked" checked={!worked} onChange={() => setWorked(false)} />
            <span>
              <strong>No, it did not help</strong>
              <span className="muted small"> tell the nurse or GP</span>
            </span>
          </label>
        </fieldset>

        <label htmlFor="outcome-note">Note (optional)</label>
        <input id="outcome-note" value={note} onChange={(e) => setNote(e.target.value)} />

        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="row-actions">
          <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          <button type="button" className="quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Overlay>
  );
}

function formatGap(minutes: number): string {
  if (minutes < 60) return `${minutes} minutes`;
  const hours = minutes / 60;
  return hours === 1 ? '1 hour' : `${Number.isInteger(hours) ? hours : hours.toFixed(1)} hours`;
}

function formatTime(iso: string): string {
  const when = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(when.getHours())}:${pad(when.getMinutes())}`;
}
